import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, parseISO, startOfWeek, startOfMonth, subDays, subWeeks, subMonths } from 'date-fns';
import type { DateFilter, Language } from '@/types';

// Tailwind class merger
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Date formatting
export function formatDate(date: string | Date, formatStr: string = 'PPP', _lang: Language = 'en'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr);
}

export function formatDateTime(date: string | Date, _lang: Language = 'en'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'PPp', { locale: _lang === 'ar' ? undefined : undefined });
}

export function formatRelativeTime(date: string | Date, lang: Language = 'en'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: lang === 'ar' ? undefined : undefined });
}

// Duration formatting
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';

  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
}

export function formatDurationLong(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '0s';

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (mins === 0) return `${secs}s`;
  if (secs === 0) return `${mins}m`;

  return `${mins}m ${secs}s`;
}

// Date filter helpers
export function getDateRangeFromPreset(preset: DateFilter['preset'], from?: string | null, to?: string | null): { from: Date; to: Date } | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (preset === 'custom' && from && to) {
    return { from: new Date(from), to: new Date(to) };
  }

  switch (preset) {
    case 'today':
      return { from: today, to: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case 'yesterday':
      return { from: subDays(today, 1), to: today };
    case 'thisWeek':
      return { from: startOfWeek(today), to: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case 'lastWeek':
      return { from: subWeeks(startOfWeek(today), 1), to: startOfWeek(today) };
    case 'thisMonth':
      return { from: startOfMonth(today), to: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case 'lastMonth':
      return { from: subMonths(startOfMonth(today), 1), to: startOfMonth(today) };
    default:
      return null;
  }
}

export function getFilterDateRange(filter: DateFilter): { from: string | null; to: string | null } {
  if (filter.preset && filter.preset !== 'all') {
    const range = getDateRangeFromPreset(filter.preset, filter.from, filter.to);
    if (range) {
      return {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      };
    }
  }

  return {
    from: filter.from ? new Date(filter.from).toISOString() : null,
    to: filter.to ? new Date(filter.to).toISOString() : null,
  };
}

// Phone number formatting
export function formatPhoneNumber(phone: string | null): string {
  if (!phone) return '-';
  return phone;
}

// Status helpers
export function getStatusLabel(status: boolean | null): { label: string; color: string; bgColor: string } {
  if (status === true) {
    return { label: 'Resolved', color: 'text-emerald-600', bgColor: 'bg-emerald-50' };
  } else if (status === false) {
    return { label: 'Pending', color: 'text-amber-600', bgColor: 'bg-amber-50' };
  }
  return { label: 'Unknown', color: 'text-gray-600', bgColor: 'bg-gray-50' };
}

// CSV Export
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  filename: string = 'export.csv',
  columns?: (keyof T)[]
): void {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const keys = columns || (Object.keys(data[0]) as (keyof T)[]);
  const headers = keys.map(key => String(key));

  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      keys.map(key => {
        const value = row[key];
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return String(value);
      }).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Debounce function
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

// Throttle function
export function throttle<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Generate unique ID
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Deep clone object
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// Truncate text
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

// Local storage helpers
export function getStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

export function setStorageItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

// Analytics helpers
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

export function groupByDate<T extends { created_at: string }>(
  records: T[],
  formatStr: string = 'yyyy-MM-dd'
): Record<string, T[]> {
  return records.reduce((groups, record) => {
    const dateKey = format(parseISO(record.created_at), formatStr);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(record);
    return groups;
  }, {} as Record<string, T[]>);
}

export function groupByHour<T extends { created_at: string }>(records: T[]): Record<number, T[]> {
  return records.reduce((groups, record) => {
    const hour = parseISO(record.created_at).getHours();
    if (!groups[hour]) {
      groups[hour] = [];
    }
    groups[hour].push(record);
    return groups;
  }, {} as Record<number, T[]>);
}

export function groupByDayOfWeek<T extends { created_at: string }>(
  records: T[]
): Record<number, T[]> {
  return records.reduce((groups, record) => {
    const day = parseISO(record.created_at).getDay();
    if (!groups[day]) {
      groups[day] = [];
    }
    groups[day].push(record);
    return groups;
  }, {} as Record<number, T[]>);
}

// Color palette for charts
export const chartColors = {
  primary: 'rgba(59, 130, 246, 1)',
  primaryLight: 'rgba(59, 130, 246, 0.2)',
  success: 'rgba(16, 185, 129, 1)',
  successLight: 'rgba(16, 185, 129, 0.2)',
  warning: 'rgba(245, 158, 11, 1)',
  warningLight: 'rgba(245, 158, 11, 0.2)',
  danger: 'rgba(239, 68, 68, 1)',
  dangerLight: 'rgba(239, 68, 68, 0.2)',
  purple: 'rgba(139, 92, 246, 1)',
  purpleLight: 'rgba(139, 92, 246, 0.2)',
  pink: 'rgba(236, 72, 153, 1)',
  pinkLight: 'rgba(236, 72, 153, 0.2)',
  teal: 'rgba(20, 184, 166, 1)',
  tealLight: 'rgba(20, 184, 166, 0.2)',
  indigo: 'rgba(99, 102, 241, 1)',
  indigoLight: 'rgba(99, 102, 241, 0.2)',
};

export const statusColors = {
  resolved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
};
